import { config } from '@/config'
import { createId, db, nowIso } from '@/modules/db'

interface GreenGoodsReportInput {
  careEventId: string
  playerId: string
  plantId: string
  energyEarned: number
}

interface GreenGoodsPayload {
  careEventId: string
  playerId: string
  plantId: string
  energyEarned: number
  submittedAt: string
}

function toPayloadRecord(payload: GreenGoodsPayload): Record<string, unknown> {
  return { ...payload }
}

function createLocalReportId(careEventId: string): string {
  return `gg_${careEventId}`
}

export async function reportCareEventToGreenGoods(input: GreenGoodsReportInput): Promise<string> {
  const timestamp = nowIso()
  const payload: GreenGoodsPayload = {
    careEventId: input.careEventId,
    playerId: input.playerId,
    plantId: input.plantId,
    energyEarned: input.energyEarned,
    submittedAt: timestamp,
  }

  if (!config.greenGoods.enabled || !config.greenGoods.endpoint) {
    const reportId = createLocalReportId(input.careEventId)
    await db.greenGoodsReports.add({
      id: createId(),
      careEventId: input.careEventId,
      playerId: input.playerId,
      status: 'queued',
      reportId,
      createdAt: timestamp,
      updatedAt: timestamp,
    })
    await db.eventLog.add({
      id: createId(),
      kind: 'green-goods-report-stub',
      payload: toPayloadRecord(payload),
      timestamp,
    })
    return reportId
  }

  try {
    const response = await fetch(config.greenGoods.endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    })
    if (!response.ok) {
      throw new Error(`Green Goods report failed (${response.status})`)
    }

    const responseBody = (await response.json()) as { reportId?: string }
    const reportId = responseBody.reportId ?? createLocalReportId(input.careEventId)
    await db.greenGoodsReports.add({
      id: createId(),
      careEventId: input.careEventId,
      playerId: input.playerId,
      status: 'submitted',
      reportId,
      createdAt: timestamp,
      updatedAt: timestamp,
    })
    await db.eventLog.add({
      id: createId(),
      kind: 'green-goods-report-submitted',
      payload: {
        ...toPayloadRecord(payload),
        reportId,
      },
      timestamp,
    })
    return reportId
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Green Goods report failed.'
    await db.greenGoodsReports.add({
      id: createId(),
      careEventId: input.careEventId,
      playerId: input.playerId,
      status: 'failed',
      error: message,
      createdAt: timestamp,
      updatedAt: timestamp,
    })
    await db.eventLog.add({
      id: createId(),
      kind: 'green-goods-report-failed',
      payload: {
        ...toPayloadRecord(payload),
        error: message,
      },
      timestamp,
    })
    throw error
  }
}
